import jsPDF from 'jspdf';
import { getLanguage } from '@/utils/languageUtils';
import type { Language } from '@/utils/languageUtils';
import { dealerPortalLabels } from '@/i18n';
import svVehicleCard from '@/i18n/locales/sv/vehicleCard.json';
import enVehicleCard from '@/i18n/locales/en/vehicleCard.json';

function getPdfLabels(lang?: Language) {
  return dealerPortalLabels(lang ?? getLanguage()).pdfForms as Record<string, unknown>;
}

/** Add BILENIA footer on every page of the document. */
function addBileniaFooter(doc: jsPDF, lang?: Language): void {
  const pdf = getPdfLabels(lang);
  const footerText = (pdf.footerGeneratedBy as string) ?? 'Dokumentet har genererats med BILENIA systemet (app.bilenia.se)';
  const totalPages = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const footerY = pageHeight - 8;
  const maxFooterWidth = typeof pageWidth === 'number' ? pageWidth - 40 : 170;
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.setFont('helvetica', 'normal');
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const lines = doc.splitTextToSize(footerText, maxFooterWidth);
    const centerX = typeof pageWidth === 'number' ? pageWidth / 2 : 105;
    lines.forEach((line: string, i: number) => {
      doc.text(line, centerX, footerY + i * 3.5, { align: 'center' });
    });
  }
  doc.setTextColor(0, 0, 0);
}

function translateVehicleValue(
  category: 'color' | 'condition',
  value: string | undefined
): string {
  if (!value?.trim()) return value || '';
  const locale = getLanguage() === 'en' ? enVehicleCard : svVehicleCard;
  const map = (locale as { color?: Record<string, string>; condition?: Record<string, string> })[category];
  const key = value.toLowerCase().replace(/\s+/g, '_');
  return (map && map[key]) || value;
}

// --- Form data interfaces (matching ServiceDialog form state shape) ---

export interface GlassFormData {
  insuranceCompany: string;
  ownerName: string;
  ownerPersonOrg: string;
  phoneDay: string;
  vatRegistered: boolean | null;
  registration: string;
  carModel: string;
  damageYear: string;
  damageMonth: string;
  damageDay: string;
  damageLocation: string;
  damageReasonStenskott: boolean;
  damageReasonInbrott: boolean;
  damageReasonOther: string;
  damageMomentGlas: boolean;
  damageMomentStold: boolean;
  damageMomentOther: string;
  signatureDate: string;
}

export interface ValuationRemark {
  id: string;
  description: string;
  deduction: string;
}

export interface ValuationFormData {
  registration: string;
  vehicle: string;
  vin: string;
  mileage: string;
  color: string;
  purpose: string;
  purposeOther: string;
  condition: string;
  extraEquipment: string;
  estimatedPurchasePrice: string;
  remarks: ValuationRemark[];
  validityPeriod: string;
  notes: string;
}

export interface FullmaktFormData {
  registration: string;
  vehicle: string;
  vin: string;
  giverName: string;
  giverPersonOrg: string;
  giverAddress: string;
  giverPostalCity: string;
  giverPhone: string;
  giverEmail: string;
  recipientName: string;
  recipientPersonOrg: string;
  recipientAddress: string;
  recipientPostalCity: string;
  recipientPhone: string;
  fullmaktType: string;
  customActions: string;
  validFrom: string;
  validTo: string;
  isUnlimited: boolean;
  signatureDate: string;
  signaturePlace: string;
}

export type SketchType = 'car' | 'road' | 'intersection' | 'highway' | 'roundabout' | 'parking';

export interface InsuranceFormData {
  registration: string;
  vehicle: string;
  vin: string;
  mileage: string;
  insuranceCompany: string;
  insuranceCompanyOther: string;
  policyNumber: string;
  damageDate: string;
  damageTime: string;
  damageLocation: string;
  damageLocationType: string;
  damageType: string;
  damageDescription: string;
  carSketchType: SketchType;
  carSketchData: string;
  sceneSketchType: SketchType;
  sceneSketchData: string;
  hasCounterpart: boolean;
  counterpartName: string;
  counterpartInsurance: string;
  counterpartRegistration: string;
  counterpartPhone: string;
  hasWitnesses: boolean;
  witnessInfo: string;
  hasPoliceReport: boolean;
  policeReportNumber: string;
  damagedParts: string;
  estimatedCost: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  signatureDate: string;
  signaturePlace: string;
}

export interface ServiceSchemaFormData {
  customerName: string;
  customerAddress: string;
  customerPostal: string;
  serviceType: string;
  serviceInterval: string;
  extraService: string;
  registration: string;
  make: string;
  model: string;
  year: string;
  engine?: string;
  transmission?: string;
  horsepower?: string;
  date: string;
  mileage: string;
  checkedItems: Record<string, boolean>;
  comments: Record<string, string>;
  signatureDate: string;
}

export interface VDNFormData {
  sellerName: string;
  sellerOrgNr: string;
  sellerAddress: string;
  sellerPostalCity: string;
  sellerPhone: string;
  sellerEmail: string;
  buyerName: string;
  buyerPersonNr: string;
  buyerAddress: string;
  buyerPostalCity: string;
  buyerPhone: string;
  buyerEmail: string;
  registration: string;
  make: string;
  model: string;
  variant: string;
  modelYear: string;
  firstRegistrationDate: string;
  vin: string;
  mileage: string;
  mileageGuaranteed: boolean;
  color: string;
  fuel: string;
  transmission: string;
  engineVolume: string;
  enginePower: string;
  environmentClass: string;
  co2Emission: string;
  numberOfOwners: string;
  importVehicle: boolean;
  importCountry: string;
  taxPaid: boolean;
  taxDebtAmount: string;
  numberOfKeys: string;
  registrationCertificate: boolean;
  lastInspectionDate: string;
  inspectionResult: string;
  inspectionRemarks: string;
  nextInspectionDate: string;
  serviceBookExists: boolean;
  serviceBookDigital: boolean;
  lastServiceDate: string;
  lastServiceMileage: string;
  lastServiceType: string;
  timingBeltChanged: boolean;
  timingBeltMileage: string;
  timingBeltDate: string;
  testDrivePerformed: boolean;
  testDriveDate: string;
  testDriveMileage: string;
  hasAccidentHistory: boolean;
  accidentDescription: string;
  hasRepaintedParts: boolean;
  repaintedPartsDescription: string;
  hasRustDamage: boolean;
  rustDescription: string;
  summerTiresIncluded: boolean;
  summerTiresBrand: string;
  summerTiresDimension: string;
  summerTiresDepth: string;
  winterTiresIncluded: boolean;
  winterTiresBrand: string;
  winterTiresDimension: string;
  winterTiresDepth: string;
  spareTireIncluded: boolean;
  isElectricOrHybrid: boolean;
  batteryCapacity: string;
  batteryHealth: string;
  chargingCableIncluded: boolean;
  estimatedRange: string;
  equipmentList: string;
  airCondition: boolean;
  navigation: boolean;
  towBar: boolean;
  sunroof: boolean;
  heatedSeats: boolean;
  parkingSensors: boolean;
  reverseCamera: boolean;
  cruiseControl: boolean;
  leatherInterior: boolean;
  engineCondition: string;
  transmissionCondition: string;
  brakeCondition: string;
  suspensionCondition: string;
  bodyCondition: string;
  interiorCondition: string;
  electricalCondition: string;
  exhaustCondition: string;
  steeringCondition: string;
  lightsCondition: string;
  remarks: string;
  knownDefects: string;
  guaranteeType: string;
  guaranteePeriod: string;
  guaranteeMileage: string;
  guaranteeConditions: string;
  reclamationInfo: string;
  price: string;
  vatIncluded: boolean;
  depositAmount: string;
  paymentMethod: string;
  financingArranged: boolean;
  deliveryDate: string;
  deliveryLocation: string;
  isDistanceSale: boolean;
  withdrawalRightInfo: string;
  signatureDate: string;
  signaturePlace: string;
}

// Service schema item keys (stable for form state); labels from locale
export const serviceSchemaItemKeys = [
  'motorolja',
  'oljefilter',
  'luftfilter',
  'kupefilter',
  'branslefilter',
  'tandstift',
  'vaxelladosolja',
  'bromsvatska',
  'kylvatska',
  'dackSkifte',
  'bromsarKlossar',
  'bromsarSkivor',
  'ljusBelysning',
  'spolarvatska',
  'viskareTorkarblad',
  'inspektionUnderrede',
  'kontrollVatskenivåer',
  'batteri',
  'sakerhetsbalten',
  'katalysatorAvgasror',
  'elsystemFelkodskontroll',
  'serviceEfterTillverkare',
  'annat',
] as const;

export function getServiceSchemaItemLabels(lang?: Language): Record<string, string> {
  const items = (getPdfLabels(lang).serviceschemaItems ?? {}) as Record<string, string>;
  const out: Record<string, string> = {};
  serviceSchemaItemKeys.forEach((k) => {
    out[k] = items[k] ?? k;
  });
  return out;
}

/** Ordered list of labels for current language (for backward compatibility: dialog can use this for display). */
export function getServiceSchemaItems(lang?: Language): string[] {
  const labels = getServiceSchemaItemLabels(lang);
  return serviceSchemaItemKeys.map((k) => labels[k] ?? k);
}

// --- PDF generators (return jsPDF instance; caller handles save/toast) ---

export function generateGlassPDF(data: GlassFormData, lang?: Language): jsPDF {
  const doc = new jsPDF();
  const pdf = getPdfLabels(lang);
  const g = (pdf.glass ?? {}) as Record<string, string>;
  const gp = (pdf.glassPdf ?? {}) as Record<string, string>;
  const yesNo = (pdf.yesNo ?? { ja: 'Ja', nej: 'Nej' }) as Record<string, string>;

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(gp.title ?? 'Skadeanmälan', 105, 20, { align: 'center' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(gp.subtitle ?? 'Förenklad skadeanmälan för bilglas', 105, 28, { align: 'center' });

  let y = 45;
  const lineHeight = 10;
  const labelX = 20;
  const valueX = 75;

  const addField = (label: string, value: string, valueXOverride?: number) => {
    const vx = valueXOverride ?? valueX;
    doc.setFont('helvetica', 'bold');
    doc.text(label, labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value || '-', vx, y);
    y += lineHeight;
  };

  addField(`${g.insuranceCompany ?? 'Försäkringsbolag'}:`, data.insuranceCompany);
  addField(`${g.ownerName ?? 'Bilägarens namn'}:`, data.ownerName);
  addField(`${g.ownerPersonOrg ?? 'Person/Org.nr'}:`, data.ownerPersonOrg);
  addField(`${g.phoneDay ?? 'Telefon dagtid'}:`, data.phoneDay);
  addField(
    `${g.vatRegistered ?? 'Moms redovisningsskyldig'}:`,
    data.vatRegistered === true ? yesNo.ja : data.vatRegistered === false ? yesNo.nej : '-',
    95
  );

  y += 5;
  addField(`${g.registration ?? 'Registreringsnummer'}:`, data.registration);
  addField(`${g.carModel ?? 'Bilmodell'}:`, data.carModel);

  y += 5;
  const damageDate = [data.damageYear, data.damageMonth, data.damageDay].filter(Boolean).join('-');
  addField(`${g.damageDate ?? 'Skadedatum'}:`, damageDate || '-');
  addField(`${g.damagePlace ?? 'Skadeplats'}:`, data.damageLocation);

  y += 5;
  const damageReasons: string[] = [];
  if (data.damageReasonStenskott) damageReasons.push(g.damageReasonStenskott ?? 'Stenskott');
  if (data.damageReasonInbrott) damageReasons.push(g.damageReasonInbrott ?? 'Inbrott');
  if (data.damageReasonOther) damageReasons.push(data.damageReasonOther);
  addField(`${g.damageReason ?? 'Skadeorsak'}:`, damageReasons.join(', ') || '-');

  const damageMoments: string[] = [];
  if (data.damageMomentGlas) damageMoments.push(g.damageMomentGlas ?? 'Glas');
  if (data.damageMomentStold) damageMoments.push(g.damageMomentStold ?? 'Stöld');
  if (data.damageMomentOther) damageMoments.push(data.damageMomentOther);
  addField(`${g.damageMoment ?? 'Skademoment'}:`, damageMoments.join(', ') || '-');

  y += 15;
  doc.setFontSize(10);
  const certText = g.certificationText ?? 'Härmed intygas riktigheten av ovanstående uppgifter samt att försäkringen var gällande vid skadetillfället och omfattar valt skademoment.';
  const certLines = doc.splitTextToSize(certText, 170);
  certLines.forEach((line: string) => {
    doc.text(line, labelX, y);
    y += 5;
  });

  y += 15;
  addField(`${g.date ?? 'Datum'}:`, data.signatureDate);

  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(`${g.signatureLabel ?? 'Försäkringstagarens eller förarens underskrift'}:`, labelX, y);
  y += 15;
  doc.line(labelX, y, 190, y);

  addBileniaFooter(doc, lang);
  return doc;
}

export function generateFullmaktPDF(data: FullmaktFormData, lang?: Language, companyName?: string | null): jsPDF {
  const doc = new jsPDF();
  const pdf = getPdfLabels(lang);
  const f = (pdf.fullmakt ?? {}) as Record<string, string>;
  const fp = (pdf.fullmaktPdf ?? {}) as Record<string, string>;
  const types = (pdf.fullmaktTypes ?? {}) as Record<string, string>;

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text(fp.title ?? 'FULLMAKT', 105, 25, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(fp.subtitle ?? 'Avseende motorfordon', 105, 32, { align: 'center' });

  let y = 45;

  doc.setFillColor(245, 245, 245);
  doc.rect(20, y, 170, 45, 'F');
  doc.setDrawColor(200, 200, 200);
  doc.rect(20, y, 170, 45);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.sectionGiverTitle ?? 'FULLMAKTSGIVARE (ägaren)', 25, y + 8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const giverFields: [string, string][] = [
    [fp.nameLabel ?? 'Namn:', data.giverName || '-'],
    [f.giverPersonOrg ?? 'Personnummer/Org.nr:', data.giverPersonOrg || '-'],
    [f.giverAddress ?? 'Adress:', data.giverAddress || '-'],
    [f.giverPostalCity ?? 'Postnummer, Ort:', data.giverPostalCity || '-'],
    [f.giverPhone ?? 'Telefon:', data.giverPhone || '-'],
    [f.giverEmail ?? 'E-post:', data.giverEmail || '-'],
  ];

  let fieldY = y + 15;
  giverFields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 25, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 70, fieldY);
    fieldY += 5;
  });

  y += 52;

  doc.setFillColor(245, 245, 245);
  doc.rect(20, y, 170, 40, 'F');
  doc.rect(20, y, 170, 40);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.sectionRecipientTitle ?? 'FULLMAKTSTAGARE (befullmäktigad)', 25, y + 8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const recipientFields: [string, string][] = [
    [f.recipientName ?? 'Namn/Företag:', data.recipientName || '-'],
    [f.recipientPersonOrg ?? 'Org.nr/Personnr:', data.recipientPersonOrg || '-'],
    [f.recipientAddress ?? 'Adress:', data.recipientAddress || '-'],
    [f.recipientPostalCity ?? 'Postnummer, Ort:', data.recipientPostalCity || '-'],
    [f.recipientPhone ?? 'Telefon:', data.recipientPhone || '-'],
  ];

  fieldY = y + 15;
  recipientFields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 25, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 70, fieldY);
    fieldY += 5;
  });

  y += 47;

  doc.setFillColor(240, 248, 255);
  doc.rect(20, y, 170, 20, 'F');
  doc.rect(20, y, 170, 20);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.vehicleTitle ?? 'FORDON', 25, y + 8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${fp.registrationLabel ?? 'Registreringsnummer'}: ${data.registration}`, 25, y + 15);
  doc.text(`${fp.vehicleLabel ?? 'Fordon'}: ${data.vehicle}`, 90, y + 15);
  if (data.vin) {
    doc.text(`${fp.vinLabel ?? 'Chassinummer'}: ${data.vin}`, 25, y + 20);
  }

  y += 28;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.scopeTitle ?? 'FULLMAKTENS OMFATTNING', 25, y);
  y += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const typeText = types[data.fullmaktType] || data.fullmaktType || '-';
  doc.text(`${fp.fullmaktAvser ?? 'Fullmakten avser:'} ${typeText}`, 25, y);
  y += 6;

  if (data.customActions) {
    const customLines = doc.splitTextToSize(
      `${fp.specialPowers ?? 'Särskilda befogenheter:'} ${data.customActions}`,
      160
    );
    doc.text(customLines, 25, y);
    y += customLines.length * 4 + 4;
  }

  y += 5;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.validityTitle ?? 'GILTIGHETSTID', 25, y);
  y += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (data.isUnlimited) {
    doc.text(fp.validityUnlimited ?? 'Fullmakten gäller tillsvidare från och med undertecknandet.', 25, y);
  } else {
    const fromDate = data.validFrom ? new Date(data.validFrom).toLocaleDateString('sv-SE') : '-';
    const toDate = data.validTo ? new Date(data.validTo).toLocaleDateString('sv-SE') : '-';
    const fromTo = (fp.validityFromTo ?? 'Fullmakten gäller från {from} till och med {to}.')
      .replace('{from}', fromDate)
      .replace('{to}', toDate);
    doc.text(fromTo, 25, y);
  }
  y += 10;

  doc.setFontSize(8);
  const legalText = fp.legalText ?? 'Undertecknad fullmaktsgivare ger härmed ovanstående fullmaktstagare rätt att i mitt/vårt namn vidta de åtgärder som anges ovan avseende ovanstående fordon. Fullmaktstagaren har rätt att underteckna erforderliga handlingar samt att företräda fullmaktsgivaren gentemot myndigheter och andra parter i dessa ärenden.';
  const legalLines = doc.splitTextToSize(legalText, 170);
  doc.text(legalLines, 20, y);
  y += legalLines.length * 4 + 15;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(fp.signatureSectionTitle ?? 'UNDERSKRIFT FULLMAKTSGIVARE', 25, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.text(`${fp.placeLabel ?? 'Ort'}: ${data.signaturePlace || '_________________'}`, 25, y);
  doc.text(
    `${f.date ?? 'Datum'}: ${data.signatureDate ? new Date(data.signatureDate).toLocaleDateString('sv-SE') : '_________________'}`,
    100,
    y
  );
  y += 15;

  doc.line(25, y, 90, y);
  doc.line(100, y, 185, y);
  y += 5;
  doc.setFontSize(8);
  doc.text(fp.signatureLabel ?? 'Underskrift fullmaktsgivare', 25, y);
  doc.text(fp.nameClarification ?? 'Namnförtydligande', 100, y);

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const fullmaktFooterText = companyName
    ? `${companyName} - ${fp.fullmaktDocumentSuffix ?? 'Fullmakt för motorfordon'}`
    : (fp.bileniaFullmakt ?? 'BILENIA - Fullmakt för motorfordon');
  doc.text(fullmaktFooterText, 105, 285, { align: 'center' });

  addBileniaFooter(doc, lang);
  return doc;
}

export function generateInsurancePDF(data: InsuranceFormData, lang?: Language, companyName?: string | null): jsPDF {
  const doc = new jsPDF();
  const pdf = getPdfLabels(lang);
  const ins = (pdf.insurance ?? {}) as Record<string, string>;
  const ip = (pdf.insurancePdf ?? {}) as Record<string, string>;
  const damageTypes = (pdf.insuranceDamageTypes ?? {}) as Record<string, string>;
  const sketchTypes = (pdf.damageSketch ?? {}) as Record<string, string>;

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text(ip.title ?? 'SKADEANMÄLAN', 105, 25, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(ip.subtitle ?? 'Försäkringsanmälan för motorfordon', 105, 32, { align: 'center' });

  let y = 45;

  doc.setFillColor(245, 245, 245);
  doc.rect(20, y, 170, 25, 'F');
  doc.setDrawColor(200, 200, 200);
  doc.rect(20, y, 170, 25);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(ip.vehicleSectionTitle ?? 'FORDONSUPPGIFTER', 25, y + 8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${ip.registrationLabel ?? ins.registration ?? 'Registreringsnummer'}: ${data.registration}`, 25, y + 16);
  doc.text(`${ip.vehicleLabel ?? 'Fordon'}: ${data.vehicle}`, 100, y + 16);
  doc.text(`${ip.vinLabel ?? 'Chassinummer'}: ${data.vin || '-'}`, 25, y + 22);
  doc.text(`${ip.mileageLabel ?? 'Mätarställning'}: ${data.mileage ? `${data.mileage} mil` : '-'}`, 100, y + 22);

  y += 32;

  doc.setFillColor(245, 245, 245);
  doc.rect(20, y, 170, 20, 'F');
  doc.rect(20, y, 170, 20);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(ip.insuranceSectionTitle ?? 'FÖRSÄKRINGSUPPGIFTER', 25, y + 8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const insuranceCompanyDisplay =
    data.insuranceCompany === 'annat' ? data.insuranceCompanyOther : data.insuranceCompany;
  doc.text(`${ins.insuranceCompany ?? 'Försäkringsbolag'}: ${insuranceCompanyDisplay || '-'}`, 25, y + 16);
  doc.text(`${ins.policyNumber ?? 'Försäkringsnummer'}: ${data.policyNumber || '-'}`, 100, y + 16);

  y += 27;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(ip.damageSectionTitle ?? 'SKADEUPPGIFTER', 25, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const damageDateTime = [data.damageDate, data.damageTime].filter(Boolean).join(' kl. ');
  doc.text(`${ip.dateAndTime ?? 'Datum och tid'}: ${damageDateTime || '-'}`, 25, y);
  y += 6;
  doc.text(`${ins.damagePlace ?? 'Skadeplats'}: ${data.damageLocation || '-'}`, 25, y);
  y += 6;

  doc.text(
    `${ip.damageTypeLabel ?? 'Typ av skada'}: ${damageTypes[data.damageType] || data.damageType || '-'}`,
    25,
    y
  );
  y += 8;

  doc.setFont('helvetica', 'bold');
  doc.text(`${ip.damageDescriptionLabel ?? 'Detaljerad beskrivning av skadehändelsen'}:`, 25, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  if (data.damageDescription) {
    const descLines = doc.splitTextToSize(data.damageDescription, 160);
    doc.text(descLines, 25, y);
    y += descLines.length * 4 + 5;
  } else {
    y += 5;
  }

  doc.text(`${ip.damagedPartsLabel ?? ins.damagedParts ?? 'Skadade delar'}: ${data.damagedParts || '-'}`, 25, y);
  y += 6;
  doc.text(`${ip.estimatedAmountLabel ?? 'Uppskattat belopp'}: ${data.estimatedCost || '-'} kr`, 25, y);
  y += 10;

  if (data.carSketchData || data.sceneSketchData) {
    doc.addPage();
    let sketchY = 20;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 51, 102);
    doc.text(ip.sketchesSectionTitle ?? 'SKISSER OCH ILLUSTRATIONER', 105, sketchY, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    sketchY += 15;

    if (data.carSketchData) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(ip.carSketchLabel ?? 'Skadeskiss på fordonet', 25, sketchY);
      sketchY += 5;

      try {
        doc.addImage(data.carSketchData, 'PNG', 25, sketchY, 80, 80);
      } catch {
        // skip if image fails
      }
      sketchY += 90;
    }

    if (data.sceneSketchData) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(ip.sceneSketchLabel ?? 'Olycksplatsskiss', 25, sketchY);
      sketchY += 5;

      const sketchTypeLabels: Record<string, string> = {
        car: sketchTypes.car ?? 'Bil (ovanifrån)',
        road: sketchTypes.road ?? 'Väg (rak sträcka)',
        intersection: sketchTypes.intersection ?? 'Korsning (fyrvägskors)',
        highway: sketchTypes.highway ?? 'Motorväg (flerfältsväg)',
        roundabout: sketchTypes.roundabout ?? 'Rondell',
        parking: sketchTypes.parking ?? 'Parkeringsplats',
      };

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${ip.placeTypeLabel ?? 'Typ av plats'}: ${sketchTypeLabels[data.sceneSketchType] || data.sceneSketchType}`,
        25,
        sketchY
      );
      sketchY += 5;

      try {
        doc.addImage(data.sceneSketchData, 'PNG', 25, sketchY, 80, 80);
      } catch {
        // skip if image fails
      }
      sketchY += 90;
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(ip.sketchLegend ?? 'Röd = Eget fordon, Blå = Motpart (om tillämpligt)', 25, sketchY);
    sketchY += 10;

    // Start next section on a new page so contact/counterpart don't overlap the sketches
    if (!data.hasCounterpart) {
      doc.addPage();
      y = 20;
    } else {
      y = 20;
    }
  }

  if (data.hasCounterpart) {
    if (!data.carSketchData && !data.sceneSketchData) {
      // continue on same page
    } else {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(ip.counterpartSectionTitle ?? 'MOTPART', 25, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${ip.nameLabel ?? ins.counterpartName ?? 'Namn'}: ${data.counterpartName || '-'}`, 25, y);
    doc.text(`${ins.counterpartPhone ?? 'Telefon'}: ${data.counterpartPhone || '-'}`, 100, y);
    y += 6;
    doc.text(`${ins.counterpartInsurance ?? 'Försäkringsbolag'}: ${data.counterpartInsurance || '-'}`, 25, y);
    doc.text(`${ins.counterpartRegistration ?? 'Reg.nr'}: ${data.counterpartRegistration || '-'}`, 100, y);
    y += 10;
  }

  if (data.hasWitnesses && data.witnessInfo) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(ip.witnessesSectionTitle ?? 'VITTNEN', 25, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const witnessLines = doc.splitTextToSize(data.witnessInfo, 160);
    doc.text(witnessLines, 25, y);
    y += witnessLines.length * 4 + 5;
  }

  if (data.hasPoliceReport) {
    doc.text(`${ip.policeReportLabel ?? ins.policeReportNumber ?? 'Polisanmälan diarienummer'}: ${data.policeReportNumber || '-'}`, 25, y);
    y += 10;
  }

  if (y > 240) {
    doc.addPage();
    y = 20;
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(ip.contactSectionTitle ?? 'KONTAKTUPPGIFTER', 25, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${ip.contactPersonLabel ?? ins.contactPerson ?? 'Kontaktperson'}: ${data.contactName || '-'}`, 25, y);
  doc.text(`${ip.emailLabel ?? 'E-post'}: ${data.contactEmail || '-'}`, 100, y);
  y += 6;
  doc.text(`${ins.contactPhoneRequired ?? ins.contactPhone ?? 'Telefon'}: ${data.contactPhone || '-'}`, 25, y);
  y += 15;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(ip.signatureSectionTitle ?? 'UNDERSKRIFT', 25, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${ip.placeLabel ?? ins.signaturePlace ?? 'Ort'}: ${data.signaturePlace || '_________________'}`, 25, y);
  doc.text(
    `Datum: ${data.signatureDate ? new Date(data.signatureDate).toLocaleDateString('sv-SE') : '_________________'}`,
    100,
    y
  );
  y += 10;
  doc.setFontSize(8);
  doc.text(ip.certificationText ?? ins.certificationText ?? 'Jag intygar att uppgifterna ovan är korrekta och sanningsenliga.', 25, y);
  y += 10;

  doc.line(25, y, 100, y);
  y += 5;
  doc.setFontSize(8);
  doc.text(ip.signatureLabel ?? 'Underskrift', 25, y);

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const suffix = (ip.insuranceDocumentSuffix ?? 'Försäkringsanmälan | Sida {page} av {total}')
      .replace('{page}', String(i))
      .replace('{total}', String(pageCount));
    const footerText = companyName ? `${companyName} - ${suffix}` : (ip.bileniaInsurance ?? 'BILENIA - Försäkringsanmälan | Sida {page} av {total}').replace('{page}', String(i)).replace('{total}', String(pageCount));
    doc.text(footerText, 105, 285, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  addBileniaFooter(doc, lang);
  return doc;
}

export function generateValuationPDF(
  data: ValuationFormData,
  companyName?: string,
  lang?: Language
): jsPDF {
  const doc = new jsPDF();
  const name = companyName?.trim() || 'BILENIA';
  const pdf = getPdfLabels(lang);
  const val = (pdf.valuation ?? {}) as Record<string, string>;
  const vp = (pdf.valuationPdf ?? {}) as Record<string, string>;

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text(`${name} ${vp.titleSuffix ?? 'Värderingsintyg'}`, 105, 25, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  let y = 40;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const introText = (val.valuationIntroText ?? `${name} har inspekterat och kontrollerat nedanstående fordon i sin helhet och intygar härmed att fordonet har ett uppskattat inköpsvärde enligt nedan. Värderingen grundas på aktuella marknadspriser samt efterfrågan från privatpersoner och företag.`).replace('{companyName}', name);
  const introLines = doc.splitTextToSize(introText, 170);
  doc.text(introLines, 20, y);
  y += introLines.length * 5 + 10;

  doc.setFillColor(245, 245, 245);
  doc.rect(20, y, 170, 50, 'F');
  doc.setDrawColor(200, 200, 200);
  doc.rect(20, y, 170, 50);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.vehicleSectionTitle ?? 'Fordonsuppgifter', 25, y + 8);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const fields: [string, string][] = [
    [vp.registrationLabel ?? 'Registreringsnummer:', data.registration],
    [vp.vehicleLabel ?? 'Fordon:', data.vehicle],
    [vp.vinLabel ?? 'Chassinummer (VIN):', data.vin || '-'],
    [vp.mileageLabel ?? 'Mätarställning:', data.mileage ? `${data.mileage} mil` : '-'],
    [vp.colorLabel ?? 'Färg:', translateVehicleValue('color', data.color) || '-'],
  ];

  let fieldY = y + 16;
  fields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 25, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 80, fieldY);
    fieldY += 7;
  });

  y += 60;

  doc.setFont('helvetica', 'bold');
  doc.text(`${vp.conditionLabel ?? val.condition ?? 'Skick'}:`, 25, y);
  doc.setFont('helvetica', 'normal');
  doc.text(translateVehicleValue('condition', data.condition) || '-', 80, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text(`${vp.purposeLabel ?? 'Värderingssyfte'}:`, 25, y);
  doc.setFont('helvetica', 'normal');
  const purposeDisplay =
    data.purpose === 'Annat' && data.purposeOther?.trim()
      ? data.purposeOther
      : (vp[`purpose${data.purpose}` as keyof typeof vp] as string | undefined) ?? data.purpose ?? '-';
  doc.text(purposeDisplay || '-', 80, y);
  y += 12;

  if (data.extraEquipment) {
    doc.setFont('helvetica', 'bold');
    doc.text(`${vp.extraEquipmentLabel ?? 'Extra utrustning'}:`, 25, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    const equipLines = doc.splitTextToSize(data.extraEquipment, 160);
    doc.text(equipLines, 25, y);
    y += equipLines.length * 5 + 8;
  }

  if (data.remarks.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text(vp.remarksTitle ?? 'Noteringar / Skador med prisavdrag:', 25, y);
    y += 8;

    doc.setFontSize(9);
    data.remarks.forEach((remark) => {
      if (remark.description || remark.deduction) {
        doc.setFont('helvetica', 'normal');
        const remarkText = `• ${remark.description || '-'}`;
        const deductionText = remark.deduction ? ` (${vp.deductionLabel ?? 'Avdrag'}: ${remark.deduction} kr)` : '';
        doc.text(remarkText + deductionText, 25, y);
        y += 6;
      }
    });
    y += 5;
  }

  if (data.notes) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${vp.notesTitle ?? 'Övriga noteringar'}:`, 25, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(data.notes, 160);
    doc.text(noteLines, 25, y);
    y += noteLines.length * 5 + 10;
  }

  doc.setFillColor(0, 51, 102);
  doc.rect(20, y, 170, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${vp.estimatedPriceLabel ?? 'Uppskattat inköpspris'}:`, 25, y + 13);
  doc.setFontSize(14);
  doc.text(`${data.estimatedPurchasePrice || '-'} SEK`, 185, y + 13, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  y += 30;

  const validityDate = new Date();
  validityDate.setDate(validityDate.getDate() + parseInt(data.validityPeriod || '30', 10));
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(
    (vp.validityLabel ?? 'Giltighetstid: {days} dagar (Giltigt t.o.m. {date})')
      .replace('{days}', data.validityPeriod || '30')
      .replace('{date}', validityDate.toLocaleDateString('sv-SE')),
    25,
    y
  );
  y += 10;

  doc.text(`${vp.issuedDateLabel ?? 'Utfärdat datum'}: ${new Date().toLocaleDateString('sv-SE')}`, 25, y);
  y += 15;

  doc.line(25, y, 100, y);
  doc.text(vp.signatureLabel ?? 'Underskrift', 25, y + 5);

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text((vp.bileniaValuation ?? '{name} Värderingsintyg').replace('{name}', name), 105, 285, { align: 'center' });

  addBileniaFooter(doc, lang);
  return doc;
}

export function generateServiceSchemaPDF(data: ServiceSchemaFormData, lang?: Language): jsPDF {
  const doc = new jsPDF();
  const pdf = getPdfLabels(lang);
  const ss = (pdf.serviceschema ?? {}) as Record<string, string>;
  const sp = (pdf.serviceschemaPdf ?? {}) as Record<string, string>;
  const itemLabels = getServiceSchemaItemLabels(lang);

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(sp.title ?? 'Serviceschema', 20, 22);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  let y = 40;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.rect(20, y, 80, 35);
  doc.text(sp.customerSectionLabel ?? 'Kunduppgifter:', 22, y + 6);
  doc.setFont('helvetica', 'bold');
  doc.text(data.customerName || '-', 22, y + 14);
  doc.setFont('helvetica', 'normal');
  doc.text(data.customerAddress || '-', 22, y + 22);
  doc.text(data.customerPostal || '-', 22, y + 30);

  doc.rect(110, y, 80, 35);
  doc.text(sp.vehicleSectionLabel ?? 'Biluppgifter:', 112, y + 6);
  doc.setFont('helvetica', 'bold');
  doc.text(`${data.make} ${data.model}`, 112, y + 14);
  doc.setFont('helvetica', 'normal');
  const vehicleInfo = [data.engine || '', data.transmission || '', data.horsepower ? `${data.horsepower}hk` : '', data.year]
    .filter(Boolean)
    .join(' ');
  doc.text(vehicleInfo, 112, y + 22);

  y += 45;

  doc.rect(20, y, 80, 40);
  doc.text(sp.serviceTypeLabel ?? ss.serviceType ?? 'Typ av service', 22, y + 8);
  doc.text(data.serviceType, 55, y + 8);
  doc.line(20, y + 12, 100, y + 12);
  doc.text(sp.serviceIntervalLabel ?? ss.serviceInterval ?? 'Serviceintervall', 22, y + 20);
  doc.text(data.serviceInterval, 55, y + 20);
  doc.line(20, y + 24, 100, y + 24);
  doc.text(sp.extraServiceLabel ?? ss.extraService ?? 'Extra Service', 22, y + 32);
  doc.text(data.extraService, 55, y + 32);

  doc.setFillColor(0, 102, 179);
  doc.rect(110, y, 80, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('', 150, y + 6);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.rect(110, y + 8, 80, 32);
  doc.text(sp.regNumberLine1 ?? 'Registrerings-', 112, y + 16);
  doc.text(sp.regNumberLine2 ?? 'nummer:', 112, y + 22);
  doc.setFont('helvetica', 'bold');
  doc.text(data.registration, 160, y + 19);
  doc.setFont('helvetica', 'normal');
  doc.line(110, y + 24, 190, y + 24);
  doc.text(sp.dateLabel ?? 'Datum:', 112, y + 30);
  doc.text(data.date.replace(/-/g, ''), 160, y + 30);
  doc.line(110, y + 32, 190, y + 32);
  doc.text(sp.mileageLabel ?? 'Mätarställning mil', 112, y + 38);
  doc.text(data.mileage, 160, y + 38);

  y += 50;

  doc.setFontSize(9);
  doc.text(sp.introText ?? 'Serviceschema enligt tillverkarens rekommendation och specifikation', 20, y);

  y += 10;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(sp.title ?? 'Serviceschema', 105, y, { align: 'center' });

  y += 8;

  doc.setFontSize(10);
  doc.setFillColor(240, 240, 240);
  doc.rect(20, y, 170, 8, 'F');
  doc.rect(20, y, 170, 8);
  doc.text(sp.doneColumn ?? 'Utförd', 22, y + 6);
  doc.text(sp.itemColumn ?? 'Servicepunkt', 40, y + 6);
  doc.text(sp.commentColumn ?? 'Kommentar/Notering', 100, y + 6);
  doc.setFont('helvetica', 'normal');

  y += 8;

  serviceSchemaItemKeys.forEach((key) => {
    const item = itemLabels[key];
    if (y > 270) {
      doc.addPage();
      y = 20;
    }

    doc.rect(20, y, 170, 8);

    const boxSize = 5;
    const boxX = 23;
    const boxY = y + 1.5;
    doc.rect(boxX, boxY, boxSize, boxSize);
    const checked = data.checkedItems[key] ?? data.checkedItems[item];
    if (checked) {
      const pad = 0.8;
      const cx = boxX + boxSize / 2;
      const bottom = boxY + boxSize - pad;
      doc.setLineWidth(0.4);
      doc.line(boxX + pad, boxY + boxSize * 0.5, cx - 0.2, bottom);
      doc.line(cx - 0.2, bottom, boxX + boxSize - pad, boxY + pad);
      doc.setLineWidth(0.2);
    }

    doc.text(item, 40, y + 6);
    doc.text(data.comments[key] ?? data.comments[item] ?? '', 100, y + 6);

    y += 8;
  });

  y += 10;

  if (y > 260) {
    doc.addPage();
    y = 20;
  }

  doc.rect(20, y, 50, 12);
  doc.setFontSize(8);
  doc.text(sp.dateBoxLabel ?? 'Datum', 22, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.text(
    data.signatureDate
      ? new Date(data.signatureDate).toLocaleDateString('sv-SE')
      : '',
    22,
    y + 11
  );
  doc.setFontSize(10);

  doc.rect(70, y, 60, 12);
  doc.text(sp.signatureLabel ?? ss.signatureDate ?? 'Signatur', 72, y + 8);

  doc.rect(130, y, 60, 12);
  doc.text(sp.title ?? 'Serviceschema', 132, y + 8);

  addBileniaFooter(doc, lang);
  return doc;
}

export function generateVDNPDF(data: VDNFormData, lang?: Language): jsPDF {
  const doc = new jsPDF();
  const pdf = getPdfLabels(lang);
  const v = (pdf.vdn ?? {}) as Record<string, string>;
  const vp = (pdf.vdnPdf ?? {}) as Record<string, string>;
  const yesNo = (pdf.yesNo ?? { ja: 'Ja', nej: 'Nej' }) as Record<string, string>;

  /** Content width and margins to avoid overflow (A4 = 210mm wide) */
  const PAGE_WIDTH_MM = 210;
  const MARGIN = 20;
  const CONTENT_WIDTH = PAGE_WIDTH_MM - MARGIN * 2;
  /** Max Y for content (footer is drawn at 290) */
  const PAGE_MAX_Y = 265;
  const NEW_PAGE_TOP = 20;

  const vdnPageBreak = (currentY: number, needMm: number): number => {
    if (currentY + needMm > PAGE_MAX_Y) {
      doc.addPage();
      return NEW_PAGE_TOP;
    }
    return currentY;
  };

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text(vp.title ?? 'KONSUMENTVERKETS VARUDEKLARATION', 105, 18, { align: 'center' });
  doc.setFontSize(11);
  doc.text(vp.subtitle ?? 'FÖR BEGAGNAD PERSONBIL', 105, 25, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const introStr =
    vp.intro ?? 'Denna varudeklaration är framtagen i enlighet med Konsumentverkets riktlinjer för försäljning av begagnade bilar';
  const introLines = doc.splitTextToSize(introStr, CONTENT_WIDTH);
  introLines.forEach((line: string, i: number) => {
    doc.text(line, PAGE_WIDTH_MM / 2, 31 + i * 4, { align: 'center' });
  });

  let y = 34 + introLines.length * 4;

  doc.setFillColor(245, 245, 245);
  doc.rect(MARGIN, y, 82, 36, 'F');
  doc.rect(108, y, 82, 36, 'F');
  doc.setDrawColor(180, 180, 180);
  doc.rect(MARGIN, y, 82, 36);
  doc.rect(108, y, 82, 36);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.seller ?? 'SÄLJARE', 25, y + 6);
  doc.text(vp.buyer ?? 'KÖPARE', 113, y + 6);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(data.sellerName || '-', 25, y + 12);
  doc.text(`${vp.orgNr ?? v.sellerOrgNr ?? 'Org.nr'}: ${data.sellerOrgNr || '-'}`, 25, y + 17);
  const sellerAddrLines = doc.splitTextToSize(data.sellerAddress || '-', 72);
  sellerAddrLines.slice(0, 2).forEach((line: string, i: number) => {
    doc.text(line, 25, y + 22 + i * 3.5);
  });
  const sellerPostalLines = doc.splitTextToSize(data.sellerPostalCity || '-', 72);
  doc.text(sellerPostalLines[0] ?? '', 25, y + 22 + Math.min(sellerAddrLines.length, 2) * 3.5);
  doc.text(`${vp.tel ?? 'Tel'}: ${data.sellerPhone || '-'}`, 25, y + 32);

  doc.text(data.buyerName || '-', 113, y + 12);
  doc.text(`${vp.personNr ?? v.buyerPersonNr ?? 'Personnr'}: ${data.buyerPersonNr || '-'}`, 113, y + 17);
  const buyerAddrLines = doc.splitTextToSize(data.buyerAddress || '-', 72);
  buyerAddrLines.slice(0, 2).forEach((line: string, i: number) => {
    doc.text(line, 113, y + 22 + i * 3.5);
  });
  const buyerPostalLines = doc.splitTextToSize(data.buyerPostalCity || '-', 72);
  doc.text(buyerPostalLines[0] ?? '', 113, y + 22 + Math.min(buyerAddrLines.length, 2) * 3.5);
  doc.text(`${vp.tel ?? 'Tel'}: ${data.buyerPhone || '-'}`, 113, y + 32);

  y += 42;

  doc.setFillColor(230, 242, 255);
  doc.rect(MARGIN, y, CONTENT_WIDTH, 38, 'F');
  doc.rect(MARGIN, y, CONTENT_WIDTH, 38);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionVehicle ?? 'FORDONSUPPGIFTER', 25, y + 6);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${v.regNr ?? 'Reg.nr'}: ${data.registration}`, 25, y + 12);
  doc.text(`${v.make ?? 'Fabrikat'}: ${data.make}`, 60, y + 12);
  const modelStr = `${v.model ?? 'Modell'}: ${data.model} ${data.variant || ''}`.trim();
  const modelLines = doc.splitTextToSize(modelStr, 85);
  modelLines.slice(0, 2).forEach((line: string, i: number) => {
    doc.text(line, 100, y + 12 + i * 3.5);
  });
  doc.text(`${v.modelYear ?? 'Årsmodell'}: ${data.modelYear}`, 150, y + 12);

  doc.text(`${v.vin ?? 'Chassinummer (VIN)'}: ${data.vin || '-'}`, 25, y + 18);
  doc.text(`${v.firstRegDate ?? '1:a reg.datum'}: ${data.firstRegistrationDate || '-'}`, 120, y + 18);

  doc.text(`${v.mileage ?? 'Mätarställning'}: ${data.mileage ? `${data.mileage} ${vp.mil ?? 'mil'}` : '-'}`, 25, y + 24);
  doc.text(`${v.mileageGuaranteed ?? 'Garanterad'}: ${data.mileageGuaranteed ? yesNo.ja : yesNo.nej}`, 70, y + 24);
  doc.text(`${v.numberOfOwners ?? 'Antal ägare'}: ${data.numberOfOwners || '-'}`, 100, y + 24);
  doc.text(`${vp.keys ?? v.numberOfKeys ?? 'Nycklar'}: ${data.numberOfKeys || '-'} st`, 140, y + 24);

  doc.text(`${v.fuel ?? 'Drivmedel'}: ${data.fuel || '-'}`, 25, y + 30);
  doc.text(`${v.transmission ?? 'Växellåda'}: ${data.transmission || '-'}`, 65, y + 30);
  doc.text(`${v.engineVolume ?? 'Motorvolym'}: ${data.engineVolume || '-'}`, 105, y + 30);
  doc.text(`${v.enginePower ?? 'Effekt'}: ${data.enginePower || '-'} hk`, 145, y + 30);

  doc.text(`${v.color ?? 'Färg'}: ${data.color || '-'}`, 25, y + 36);
  doc.text(`${v.environmentClass ?? 'Miljöklass'}: ${data.environmentClass || '-'}`, 65, y + 36);
  doc.text(`CO2: ${data.co2Emission || '-'} g/km`, 110, y + 36);
  doc.text(`${vp.regCertificate ?? 'Reg.bevis'}: ${data.registrationCertificate ? yesNo.ja : yesNo.nej}`, 150, y + 36);

  y += 44;

  y = vdnPageBreak(y, 25);
  if (data.importVehicle || !data.taxPaid) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(vp.sectionImportTax ?? 'IMPORT OCH SKATT', 25, y);
    y += 5;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    if (data.importVehicle) {
      doc.text((vp.importFrom ?? 'Importfordon: Ja - från {country}').replace('{country}', data.importCountry || (vp.unknownCountry ?? 'okänt land')), 25, y);
      y += 4;
    }
    if (!data.taxPaid) {
      doc.text((vp.taxNotPaid ?? 'Fordonsskatt ej betald. Skuld: {amount} kr').replace('{amount}', data.taxDebtAmount || '-'), 25, y);
      y += 4;
    }
    y += 4;
  }

  y = vdnPageBreak(y, 25);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionInspection ?? 'BESIKTNING', 25, y);
  y += 5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${vp.lastInspection ?? 'Senaste besiktning'}: ${data.lastInspectionDate || '-'}`, 25, y);
  const inspectionResultKey = data.inspectionResult;
  const inspectionResultLabel =
    inspectionResultKey === 'approved'
      ? (vp.inspectionResultApproved ?? 'Godkänd')
      : inspectionResultKey === 'approved_with_remarks'
        ? (vp.inspectionResultApprovedWithRemarks ?? 'Godkänd med anmärkning')
        : inspectionResultKey === 'not_approved'
          ? (vp.inspectionResultNotApproved ?? 'Ej godkänd')
          : (data.inspectionResult || '-');
  doc.text(`${vp.result ?? 'Resultat'}: ${inspectionResultLabel}`, 80, y);
  doc.text(`${vp.nextInspection ?? 'Nästa besiktning'}: ${data.nextInspectionDate || '-'}`, 130, y);
  y += 5;
  if (data.inspectionRemarks) {
    const inspLines = doc.splitTextToSize(`${vp.inspectionRemarks ?? 'Anmärkningar'}: ${data.inspectionRemarks}`, CONTENT_WIDTH);
    doc.text(inspLines, 25, y);
    y += inspLines.length * 3.5;
  }
  y += 4;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionService ?? 'SERVICE OCH UNDERHÅLL', 25, y);
  y += 5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const serviceBookText = data.serviceBookExists
    ? (data.serviceBookDigital ? (vp.serviceBookYesDigital ?? 'Ja (digital)') : (vp.serviceBookYesPhysical ?? 'Ja (fysisk)'))
    : (vp.serviceBookNo ?? 'Nej');
  doc.text(`${vp.serviceBook ?? 'Servicebok'}: ${serviceBookText}`, 25, y);
  doc.text(
    (vp.lastServiceAt ?? 'Senaste service: {date} vid {mileage} mil')
      .replace('{date}', data.lastServiceDate || '-')
      .replace('{mileage}', data.lastServiceMileage || '-'),
    80,
    y
  );
  y += 5;
  if (data.timingBeltChanged) {
    doc.text(
      (vp.timingBeltAt ?? 'Kamrem bytt: {date} vid {mileage} km')
        .replace('{date}', data.timingBeltDate || '-')
        .replace('{mileage}', data.timingBeltMileage || '-'),
      25,
      y
    );
    y += 5;
  }
  doc.text(
    `${vp.testDrivePerformed ?? 'Provkörning utförd'}: ${data.testDrivePerformed ? yesNo.ja : yesNo.nej}${data.testDrivePerformed ? ` (${data.testDriveDate || '-'})` : ''}`,
    25,
    y
  );
  y += 8;

  y = vdnPageBreak(y, 35);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionDamageRepairs ?? 'TIDIGARE SKADOR OCH REPARATIONER', 25, y);
  y += 5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${vp.accidentVehicle ?? 'Olycksfordon'}: ${data.hasAccidentHistory ? yesNo.ja : yesNo.nej}`, 25, y);
  doc.text(`${vp.repaintedParts ?? 'Omlackerade delar'}: ${data.hasRepaintedParts ? yesNo.ja : yesNo.nej}`, 80, y);
  doc.text(`${vp.rustDamage ?? 'Rostskador'}: ${data.hasRustDamage ? yesNo.ja : yesNo.nej}`, 140, y);
  y += 5;

  if (data.hasAccidentHistory && data.accidentDescription) {
    const accLines = doc.splitTextToSize(`${vp.accidentDescription ?? 'Skadebeskrivning'}: ${data.accidentDescription}`, CONTENT_WIDTH);
    doc.text(accLines, 25, y);
    y += accLines.length * 3.5;
  }
  if (data.hasRepaintedParts && data.repaintedPartsDescription) {
    const repLines = doc.splitTextToSize(`${vp.repaintedDescription ?? 'Omlackering'}: ${data.repaintedPartsDescription}`, CONTENT_WIDTH);
    doc.text(repLines, 25, y);
    y += repLines.length * 3.5;
  }
  if (data.hasRustDamage && data.rustDescription) {
    const rustLines = doc.splitTextToSize(`${vp.rustDescription ?? 'Rost'}: ${data.rustDescription}`, CONTENT_WIDTH);
    doc.text(rustLines, 25, y);
    y += rustLines.length * 3.5;
  }
  y += 4;

  y = vdnPageBreak(y, 30);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionTechnicalCondition ?? 'TEKNISKT SKICK (bedömning av säljaren)', 25, y);
  y += 5;

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  const conditionItems: [string, string][] = [
    [vp.conditionEngine ?? 'Motor', data.engineCondition],
    [vp.conditionTransmission ?? 'Växellåda', data.transmissionCondition],
    [vp.conditionBrakes ?? 'Bromsar', data.brakeCondition],
    [vp.conditionSuspension ?? 'Fjädring', data.suspensionCondition],
    [vp.conditionBody ?? 'Kaross', data.bodyCondition],
    [vp.conditionInterior ?? 'Interiör', data.interiorCondition],
    [vp.conditionElectrical ?? 'El-system', data.electricalCondition],
    [vp.conditionExhaust ?? 'Avgassystem', data.exhaustCondition],
    [vp.conditionSteering ?? 'Styrning', data.steeringCondition],
    [vp.conditionLights ?? 'Belysning', data.lightsCondition],
  ];

  let col = 0;
  conditionItems.forEach(([label, value]) => {
    const xPos = 25 + col * 34;
    doc.text(`${label}: ${value}`, xPos, y);
    col++;
    if (col === 5) {
      col = 0;
      y += 4;
    }
  });
  if (col !== 0) y += 4;
  y += 4;

  y = vdnPageBreak(y, 30);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionTyresWheels ?? 'DÄCK OCH HJUL', 25, y);
  y += 5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${vp.summerTyres ?? 'Sommardäck'}: ${data.summerTiresIncluded ? yesNo.ja : yesNo.nej}`, 25, y);
  if (data.summerTiresIncluded) {
    doc.text(
      `${data.summerTiresBrand || '-'} ${data.summerTiresDimension || ''} ${vp.pattern ?? 'Mönster'}: ${data.summerTiresDepth || '-'}mm`,
      65,
      y
    );
  }
  y += 5;
  doc.text(`${vp.winterTyres ?? 'Vinterdäck'}: ${data.winterTiresIncluded ? yesNo.ja : yesNo.nej}`, 25, y);
  if (data.winterTiresIncluded) {
    doc.text(
      `${data.winterTiresBrand || '-'} ${data.winterTiresDimension || ''} ${vp.pattern ?? 'Mönster'}: ${data.winterTiresDepth || '-'}mm`,
      65,
      y
    );
  }
  y += 5;
  doc.text(`${vp.spareWheel ?? 'Reservhjul'}: ${data.spareTireIncluded ? yesNo.ja : yesNo.nej}`, 25, y);
  y += 6;

  y = vdnPageBreak(y, 25);
  if (data.isElectricOrHybrid) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(vp.sectionEvInfo ?? 'ELBIL / HYBRIDINFORMATION', 25, y);
    y += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${vp.batteryCapacity ?? 'Batterikapacitet'}: ${data.batteryCapacity || '-'} kWh`, 25, y);
    doc.text(`${vp.batteryHealth ?? 'Batterihälsa'}: ${data.batteryHealth || '-'}%`, 80, y);
    doc.text(`${vp.range ?? 'Räckvidd: ca'} ${data.estimatedRange || '-'} ${vp.km ?? 'km'}`, 130, y);
    y += 5;
    doc.text(`${vp.chargingCableIncluded ?? 'Laddkabel medföljer'}: ${data.chargingCableIncluded ? yesNo.ja : yesNo.nej}`, 25, y);
    y += 6;
  }

  y = vdnPageBreak(y, 40);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionEquipment ?? 'UTRUSTNING', 25, y);
  y += 5;

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  if (data.equipmentList) {
    const eqLines = doc.splitTextToSize(data.equipmentList, CONTENT_WIDTH);
    doc.text(eqLines, 25, y);
    y += eqLines.length * 3.5;
  } else {
    doc.text(vp.noEquipmentGiven ?? 'Ingen utrustning angiven i fordonskortet.', 25, y);
    y += 3.5;
  }
  y += 4;

  y = vdnPageBreak(y, 30);
  if (data.remarks || data.knownDefects) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(vp.sectionRemarksDefects ?? 'ANMÄRKNINGAR OCH KÄNDA BRISTER', 25, y);
    y += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    if (data.knownDefects) {
      doc.setTextColor(180, 0, 0);
      const defectLines = doc.splitTextToSize(`${vp.knownDefects ?? 'Kända brister'}: ${data.knownDefects}`, CONTENT_WIDTH);
      doc.text(defectLines, 25, y);
      y += defectLines.length * 3.5;
      doc.setTextColor(0, 0, 0);
    }
    if (data.remarks) {
      const remarkLines = doc.splitTextToSize(`${vp.otherRemarks ?? 'Övriga anmärkningar'}: ${data.remarks}`, 160);
      doc.text(remarkLines, 25, y);
      y += remarkLines.length * 3.5;
    }
    y += 4;
  }

  y = vdnPageBreak(y, 55);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionGuarantee ?? 'GARANTI', 25, y);
  y += 5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const guaranteeLabels: Record<string, string> = {
    konsument6: vp.guaranteeConsumer6 ?? 'Konsumentgaranti 6 månader',
    konsument12: vp.guaranteeConsumer12 ?? 'Konsumentgaranti 12 månader',
    trafiksakerhet: vp.guaranteeTrafficSafety ?? 'Trafiksäkerhetsgaranti',
    ingen: vp.guaranteeNone ?? 'Ingen garanti (utöver lagstadgad reklamationsrätt)',
    annan: vp.guaranteeOther ?? 'Annan garanti',
  };
  doc.text(
    `${vp.guaranteeType ?? 'Garantityp'}: ${guaranteeLabels[data.guaranteeType] || data.guaranteeType || (vp.notSpecified ?? 'Ej angiven')}`,
    25,
    y
  );
  if (data.guaranteePeriod || data.guaranteeMileage) {
    doc.text(`${vp.guaranteeApplies ?? 'Gäller'}: ${data.guaranteePeriod || '-'} / ${data.guaranteeMileage || '-'} ${vp.km ?? 'km'}`, 100, y);
  }
  y += 5;
  if (data.guaranteeConditions) {
    const guarLines = doc.splitTextToSize(`${vp.guaranteeConditions ?? 'Villkor'}: ${data.guaranteeConditions}`, CONTENT_WIDTH);
    doc.text(guarLines, 25, y);
    y += guarLines.length * 3.5;
  }
  y += 4;

  doc.setFillColor(255, 250, 230);
  doc.rect(MARGIN, y, CONTENT_WIDTH, 16, 'F');
  doc.rect(MARGIN, y, CONTENT_WIDTH, 16);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionReclamation ?? 'REKLAMATIONSRÄTT ENLIGT KONSUMENTKÖPLAGEN', 25, y + 5);
  doc.setFont('helvetica', 'normal');
  const reclamLines = doc.splitTextToSize(
    data.reclamationInfo ||
      (vp.reclamationDefault ?? 'Enligt konsumentköplagen har köparen rätt att reklamera fel på bilen i tre år från köpet.'),
    CONTENT_WIDTH
  );
  doc.text(reclamLines, 25, y + 10);
  y += 20;

  y = vdnPageBreak(y, 55);
  doc.setFillColor(0, 51, 102);
  doc.rect(MARGIN, y, CONTENT_WIDTH, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionPrice ?? 'PRIS:', 25, y + 9);
  doc.text(
    `${data.price || '-'} SEK ${data.vatIncluded ? (vp.vatIncluded ?? '(inkl. moms)') : (vp.vatExcluded ?? '(exkl. moms)')}`,
    180,
    y + 9,
    { align: 'right' }
  );
  doc.setTextColor(0, 0, 0);
  y += 18;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  if (data.depositAmount) {
    doc.text(`${vp.deposit ?? 'Handpenning'}: ${data.depositAmount} kr`, 25, y);
  }
  doc.text(`${vp.paymentMethod ?? 'Betalningssätt'}: ${data.paymentMethod || '-'}`, 80, y);
  doc.text(`${vp.financing ?? 'Finansiering'}: ${data.financingArranged ? yesNo.ja : yesNo.nej}`, 140, y);
  y += 5;
  doc.text(`${vp.deliveryDate ?? 'Leveransdatum'}: ${data.deliveryDate || '-'}`, 25, y);
  doc.text(`${vp.deliveryLocation ?? 'Leveransplats'}: ${data.deliveryLocation || '-'}`, 80, y);
  y += 8;

  if (data.isDistanceSale) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(vp.sectionWithdrawal ?? 'ÅNGERRÄTT (DISTANSKÖP)', 25, y);
    y += 5;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    const withdrawalText =
      data.withdrawalRightInfo ||
      (vp.withdrawalDefault ?? 'Vid köp på distans har du enligt distansavtalslagen 14 dagars ångerrätt.');
    const withdrawLines = doc.splitTextToSize(withdrawalText, CONTENT_WIDTH);
    doc.text(withdrawLines, 25, y);
    y += withdrawLines.length * 3.5 + 4;
  }

  y = vdnPageBreak(y, 50);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(vp.sectionSignatures ?? 'UNDERSKRIFTER', 25, y);
  y += 6;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`${vp.place ?? 'Ort'}: ${data.signaturePlace || '_________________'}`, 25, y);
  doc.text(
    `${vp.date ?? 'Datum'}: ${data.signatureDate ? new Date(data.signatureDate).toLocaleDateString('sv-SE') : '_________________'}`,
    80,
    y
  );
  y += 10;

  doc.line(25, y, 95, y);
  doc.line(105, y, 185, y);
  y += 4;
  doc.setFontSize(6);
  doc.text(vp.sellerSignature ?? 'Säljarens underskrift', 25, y);
  doc.text(vp.buyerSignature ?? 'Köparens underskrift', 105, y);
  y += 3;
  doc.text(vp.nameClarification ?? 'Namnförtydligande:', 25, y);
  doc.text(vp.nameClarification ?? 'Namnförtydligande:', 105, y);

  const pageCount = doc.getNumberOfPages();
  const footerTemplate = vp.footerPage ?? 'Konsumentverkets Varudeklaration för begagnad personbil | BILENIA | Sida {current} av {total}';
  const companyName = (data.sellerName || '').trim() || 'BILENIA';
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(6);
    doc.setTextColor(100, 100, 100);
    const footerText = footerTemplate
      .replace('{current}', String(i))
      .replace('{total}', String(pageCount))
      .replace(/\|\s*BILENIA\s*\|/g, `| ${companyName} |`);
    const footerLines = doc.splitTextToSize(footerText, CONTENT_WIDTH);
    footerLines.forEach((line: string, lineIndex: number) => {
      doc.text(line, PAGE_WIDTH_MM / 2, 290 + lineIndex * 3.5, { align: 'center' });
    });
    doc.setTextColor(0, 0, 0);
  }

  /* VDN has its own footer above; do not add generic BILENIA footer (would overlap) */
  return doc;
}
